import { clearActiveSellerGroupId } from '@seller-hermes/features/portal/portal-local-context';
import { leavePageTo } from '@seller-hermes/utils/chunk-error';
import { purgeLegacyCognitoStorage } from '@seller-hermes/utils/cognito-storage-migration';
import { LOGOUT_MARKER_COOKIE } from '@seller-hermes/utils/logout-marker';
import { isStandaloneLoginRuntime } from '@seller-hermes/utils/runtime';

export { LOGOUT_MARKER_COOKIE };

const LOGOUT_MARKER_MAX_AGE_SECONDS = 60 * 60 * 4;
const LOGOUT_MARKER_CHECK_INTERVAL_MS = 10_000;
const COOKIE_DOMAIN = process.env['NX_COOKIE_DOMAIN'] ?? '.abbiamolog.com';

let redirectingToLogin = false;
let logoutMarker: string | null = null;
let monitorId: number | null = null;
let onSessionLoss: () => void;
let lifecycleHandlersInstalled = false;

const canUseSharedCookieDomain = () =>
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith(COOKIE_DOMAIN.replace(/^\./, ''));

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
};

export const publishLogoutMarker = (): void => {
  if (typeof document === 'undefined') return;

  const attributes = [
    `${LOGOUT_MARKER_COOKIE}=${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2)}`,
    'Path=/',
    `Max-Age=${LOGOUT_MARKER_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];

  if (canUseSharedCookieDomain()) {
    attributes.push(`Domain=${COOKIE_DOMAIN}`, 'Secure');
  }

  document.cookie = attributes.join('; ');
};

export const redirectAfterSessionLoss = (
  options: { purgeLegacyStorage?: boolean } = {},
): void => {
  if (redirectingToLogin) return;

  redirectingToLogin = true;
  // Só em logout deliberado. Em tokenRefresh_failure (que pode ser blip de
  // rede) o localStorage precisa sobreviver: é a rede de segurança do rollback.
  if (options.purgeLegacyStorage) purgeLegacyCognitoStorage();
  clearActiveSellerGroupId();
  leavePageTo(
    isStandaloneLoginRuntime() ? '/login' : process.env['NX_PORTAL_URL'] ?? '/',
  );
};

// Marker publicado por outro produto do domínio = o usuário deslogou de
// verdade, então o rastro do localStorage legado pode ir junto.
const handleLogoutMarkerLoss = (): void =>
  redirectAfterSessionLoss({ purgeLegacyStorage: true });

export const stopLogoutMarkerMonitor = (): void => {
  if (monitorId === null) return;

  window.clearInterval(monitorId);
  monitorId = null;
};

const checkLogoutMarker = (): void => {
  const nextMarker = getCookieValue(LOGOUT_MARKER_COOKIE);
  if (nextMarker === logoutMarker) return;

  logoutMarker = nextMarker;
  stopLogoutMarkerMonitor();
  onSessionLoss();
};

const handleVisibilityOrFocus = (): void => {
  if (monitorId === null) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

  checkLogoutMarker();
};

const installLifecycleHandlers = (): void => {
  if (lifecycleHandlersInstalled) return;

  lifecycleHandlersInstalled = true;
  window.addEventListener('pagehide', stopLogoutMarkerMonitor);
  window.addEventListener('pageshow', () => startLogoutMarkerMonitor());
  window.addEventListener('focus', handleVisibilityOrFocus);
  document.addEventListener('visibilitychange', handleVisibilityOrFocus);
};

export const startLogoutMarkerMonitor = (
  onLoss: () => void = handleLogoutMarkerLoss,
  intervalMs = LOGOUT_MARKER_CHECK_INTERVAL_MS,
): void => {
  if (typeof window === 'undefined' || monitorId !== null) return;

  logoutMarker = getCookieValue(LOGOUT_MARKER_COOKIE);
  onSessionLoss = onLoss;
  installLifecycleHandlers();
  monitorId = window.setInterval(
    checkLogoutMarker,
    intervalMs,
  );
};
